import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import calendar from 'dayjs/plugin/calendar';
import duration from 'dayjs/plugin/duration';
import isToday from 'dayjs/plugin/isToday';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import updateLocale from 'dayjs/plugin/updateLocale';
import utc from 'dayjs/plugin/utc';
export type { Dayjs } from 'dayjs';

const defaultTimeZone = 'Europe/Paris';

dayjs.extend(calendar);
dayjs.extend(duration);
dayjs.extend(isToday);
dayjs.extend(relativeTime);
dayjs.extend(timezone);
dayjs.extend(updateLocale);
dayjs.extend(utc);

dayjs.updateLocale('en', {
    relativeTime: {
        future: 'in %s',
        past: '%s ago',
        s: 'a min. ago',
        m: 'a min. ago',
        mm: '%d min. ago',
        h: 'an hour ago',
        hh: '%d hours ago',
        d: 'Yesterday',
        dd: '%d days ago',
        M: 'a month ago',
        MM: '%d months ago',
        y: 'a year ago',
        yy: '%d years ago',
    },
});

dayjs.tz.setDefault(defaultTimeZone);

const dateFormat = 'D MMM YYYY';
const timeFormat = 'h:mm A [(PT)]';
const timeSecFormat = 'h:mm:ss A [(PT)]';

const getLanguage = () => 'en';

type DateType = Date | string | number | null | undefined;

export const dayjstz = (date: DateType) => dayjs(date)?.tz();

export const diplayDate = (date: Date) => {
    if (!date || !dayjstz(date).isValid()) {
        return '';
    }

    const duration = differenceInHours(date, Date.now());
    if (duration <= 0 && duration > -48) {
        return dayjstz(date).locale(getLanguage()).fromNow(true);
    }

    return formatDate(date);
};

export const differenceInHours = (date1: DateType, date2: DateType) => {
    return dayjs(date1).diff(date2, 'hour');
};

export const formatDatetime = (date: DateType) => {
    return date && dayjstz(date).locale(getLanguage()).calendar(dayjstz(Date.now()), {
        sameDay: `[today at] ${timeFormat}`,
        nextDay: `[tomorrow at] ${timeFormat}`,
        nextWeek: `dddd [at] ${timeFormat}`,
        lastDay: `[yesterday at] ${timeFormat}`,
        lastWeek: `[last] dddd [at] ${timeFormat}`,
        sameElse: `${dateFormat} [at] ${timeFormat}`,
    });
};

export const formatYear = (date: DateType) => date && dayjstz(date).locale(getLanguage()).format('YYYY');

export const formatMonth = (date: DateType) => date && dayjstz(date).locale(getLanguage()).format('MMM');

export const formatDay = (date: DateType) => date && dayjstz(date).locale(getLanguage()).format('D');

export const formatTime = (date: DateType) => date && dayjstz(date).locale(getLanguage()).format(timeFormat);

export const formatTimeSec = (date: DateType) => date && dayjstz(date).locale(getLanguage()).format(timeSecFormat);

export const formatDate = (date: DateType, formatDate = dateFormat) => dayjstz(date).locale(getLanguage()).format(formatDate);

export const formatShortDatetime = (date: DateType) => date && (dayjstz(date).isToday() ? formatTime(date) : formatDate(date));
